import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { PERMISSOES } from '../constants/permissions';
import { ROLES } from '../constants/roles';

/**
 * Mapeamento de permissões por tipo de usuário (role).
 * Este objeto define quais permissões cada tipo de usuário do sistema TugasCoins possui.
 * Ele serve como a fonte de verdade para a lógica de autorização no frontend,
 * garantindo que as funcionalidades sejam acessíveis apenas por usuários com os privilégios corretos.
 */
const ROLE_PERMISSIONS_MAP: Record<string, string[]> = {
  [ROLES.ADMINISTRADOR]: Object.values(PERMISSOES), // Administradores possuem todas as permissões do sistema.
  [ROLES.GERENTE]: [
    PERMISSOES.criar_usuario,
    PERMISSOES.ler_usuario,
    PERMISSOES.atualizar_usuario,
    PERMISSOES.criar_cliente,
    PERMISSOES.ler_cliente,
    PERMISSOES.atualizar_cliente,
    PERMISSOES.gerar_relatorio,
    PERMISSOES.criar_transacao,
    // Gerentes podem ter acesso a campanhas e unidades, mas as permissões específicas
    // para essas entidades não estão listadas em PERMISSOES no momento.
    // Se novas permissões forem adicionadas em PERMISSOES, este mapa deve ser expandido.
  ],
  [ROLES.SUPERVISOR]: [
    PERMISSOES.ler_usuario,
    PERMISSOES.ler_cliente,
    PERMISSOES.atualizar_cliente,
    PERMISSOES.gerar_relatorio,
    PERMISSOES.criar_transacao,
  ],
  [ROLES.OPERADOR]: [
    PERMISSOES.ler_cliente,
    PERMISSOES.criar_transacao,
  ],
  // O tipo 'cliente' não está explicitamente em ROLES, mas se um cliente autenticado
  // precisasse de permissões específicas para o aplicativo do cliente, elas seriam definidas aqui.
  // No contexto atual, este hook foca em permissões administrativas e operacionais.
};

/**
 * Hook personalizado `usePermissions` para verificar as permissões do usuário autenticado.
 * Este hook oferece uma maneira elegante e centralizada de controlar o acesso a funcionalidades
 * e elementos da interface com base no tipo de usuário logado.
 *
 * Ele se integra com o `AuthContext` para obter as informações do usuário atual
 * e utiliza um mapeamento interno para determinar quais ações o usuário pode realizar.
 *
 * @returns Um objeto contendo a função `hasPermission`.
 *          `hasPermission(permission: string)`: Retorna `true` se o usuário autenticado
 *          possuir a permissão especificada, `false` caso contrário.
 *
 * @throws {Error} Se `usePermissions` for utilizado fora de um `AuthProvider`,
 *                 indicando uma configuração incorreta do contexto de autenticação.
 */
export const usePermissions = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('O hook usePermissions deve ser utilizado dentro de um AuthProvider para funcionar corretamente.');
  }

  const { usuario } = context;

  /**
   * Verifica se o usuário autenticado possui uma permissão específica.
   *
   * @param permission A string que representa a permissão a ser verificada (ex: PERMISSOES.criar_usuario).
   * @returns `true` se o usuário tiver a permissão, `false` caso contrário.
   */
  const hasPermission = (permission: string): boolean => {
    // Se não houver usuário autenticado, ele não possui nenhuma permissão.
    if (!usuario) {
      return false;
    }

    // Obtém as permissões associadas ao tipo de usuário atual.
    const permissoesDoUsuario = ROLE_PERMISSIONS_MAP[usuario.tipo_usuario];

    // Se o tipo de usuário não estiver mapeado ou não tiver permissões definidas,
    // assume-se que ele não possui a permissão solicitada.
    if (!permissoesDoUsuario) {
      return false;
    }

    // Verifica se a permissão específica está incluída na lista de permissões do usuário.
    return permissoesDoUsuario.includes(permission);
  };

  return { hasPermission };
};